/**
 * Axis 相关
 *
 * @see https://echarts.apache.org/zh/option.html#xAxis
 * @see https://echarts.apache.org/zh/option.html#yAxis
 */
import type { EChartsExtensionInstallRegisters } from 'echarts';
import Axis from 'echarts/lib/coord/Axis';
import type AxisType from 'echarts/types/src/coord/Axis';
import type AxisModel from 'echarts/types/src/coord/cartesian/AxisModel';
import Grid from 'echarts/lib/coord/cartesian/Grid';
import type GridType from 'echarts/types/src/coord/cartesian/Grid';
import type GlobalModel from 'echarts/types/src/model/Global';
import type ExtensionAPI from 'echarts/types/src/core/ExtensionAPI';
import type Axis2D from 'echarts/types/src/coord/cartesian/Axis2D';
import { round } from 'echarts/lib/util/number';
// eslint-disable-next-line import/no-extraneous-dependencies
import { parsePercent } from 'zrender/lib/contain/text';
import type { Group } from 'zrender';
import { calcScientificNumber, isInt, nice } from '../../util/math';

// ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

/**
 * * 为轴提供一个分割线条数的配置项 `dvSplitLineNumber`，多个轴 0 刻度对齐的配置项 `dvAlignZeroTick`
 *
 * - 区别于官方的 splitNumber 配置项，并具有比 splitNumber 更高的优先级
 * - 当前实现仅对 type: 'value' 轴有效
 *
 * - 1. 分割线数目固定
 * - 2. 要有 0 刻度
 * - 3. 多个轴的 0 刻度对齐
 */

function isValidExtent(axis: Axis2D) {
  const dataExtent = axis.scale.getExtent();
  return (
    Number.isFinite(dataExtent[0]) &&
    Number.isFinite(dataExtent[1]) &&
    dataExtent[0] <= dataExtent[1]
  );
}

function isUserConfig(axis: Axis2D) {
  return (
    (typeof axis?.model?.get('interval') !== 'undefined' &&
      (typeof axis?.model?.get('__cacheInterval') === 'undefined' ||
        axis?.model?.get('interval') !== axis?.model?.get('__cacheInterval'))) ||
    (typeof axis?.model?.get('min') !== 'undefined' &&
      (typeof axis?.model?.get('__cacheMin') === 'undefined' ||
        axis?.model?.get('min') !== axis?.model?.get('__cacheMin'))) ||
    (typeof axis?.model?.get('max') !== 'undefined' &&
      (typeof axis?.model?.get('__cacheMax') === 'undefined' ||
        axis?.model?.get('max') !== axis?.model?.get('__cacheMax')))
  );
}

function needProcessSplitLineNumber(axis: Axis2D) {
  return !(
    !isValidExtent(axis) ||
    !axis?.model?.get('splitLine')?.show ||
    axis?.scale?.type !== 'interval' ||
    !(Number.isFinite(axis.scale.getExtent()[0]) && Number.isFinite(axis.scale.getExtent()[1])) ||
    // ! 需要和其它配置项一起生效
    (typeof axis?.model?.get('dvSplitLineNumber') === 'undefined' &&
      !axis.model.get('alignTicks') &&
      !axis?.model?.get('dvAlignZeroTick')) ||
    // ! 用户侧配置优先级更高
    isUserConfig(axis)
  );
}

function clearCache(axis: Axis2D) {
  delete axis.model.option.__cacheMin;
  delete axis.model.option.__cacheMax;
  delete axis.model.option.__cacheInterval;
}

function setAndUpdateCache(axis: Axis2D, { min, max, interval }) {
  if (Number.isNaN(min) || Number.isNaN(max) || Number.isNaN(interval)) {
    return;
  }

  axis.model.option.min = min;
  axis.model.option.max = max;
  axis.model.option.interval = interval;

  axis.model.option.__cacheMin = min;
  axis.model.option.__cacheMax = max;
  axis.model.option.__cacheInterval = interval;
}

/**
 * 计算轴的两侧留白空间
 *
 * ! 代码拷贝自 echarts 源码，保证和原有的 boundaryGap 配置项解析逻辑一致
 */
function calculateBoundaryGap(axis: Axis2D) {
  let boundaryGapInner = [0, 0];

  const boundaryGap = axis.model.get('boundaryGap');
  const boundaryGapArr = Array.isArray(boundaryGap)
    ? boundaryGap
    : [boundaryGap || 0, boundaryGap || 0];

  if (typeof boundaryGapArr[0] === 'boolean' || typeof boundaryGapArr[1] === 'boolean') {
    boundaryGapInner = [0, 0];
  } else {
    boundaryGapInner = [parsePercent(boundaryGapArr[0], 1), parsePercent(boundaryGapArr[1], 1)];
  }

  return boundaryGapInner;
}

/**
 * 获取包含 boundaryGap 的轴范围
 */
function getExtentWithBoundaryGap(axis: Axis2D): [number, number] {
  const dataExtent = axis.scale.getExtent();
  const dataSpan = dataExtent[1] - dataExtent[0];
  // ! 这里的实现和 ecahrts 保持一致，取数据原始范围的百分比
  const boundaryGapInner = calculateBoundaryGap(axis).map(item => dataSpan * item);
  let [min, max] = [dataExtent[0] + -boundaryGapInner[0], dataExtent[1] + boundaryGapInner[1]];
  const needCrossZero = axis.model.getNeedCrossZero && axis.model.getNeedCrossZero();

  // fix cross zero tick
  if (needCrossZero) {
    if (max < 0) {
      max = 0;
    } else if (min > 0) {
      min = 0;
    }
  }

  return [min, max];
}

function isValidSplitLineNumber(dvSplitLineNumber: number) {
  return !(
    typeof dvSplitLineNumber === 'undefined' ||
    Number.isNaN(dvSplitLineNumber) ||
    dvSplitLineNumber <= 1
  );
}

function niceAxisExtentOnOneSplitLine({
  originExtent,
  min,
  max,
  interval
}: {
  originExtent: [number, number];
  min: number;
  max: number;
  interval: number;
}) {
  // ! 对于 splitNumber === 1 这种特殊情况，需要按 min，max 直接确定最终范围
  max = max === 0 ? 0 : nice(max);

  if (min === 0) {
    min = 0;
  } else if (min < 0) {
    min = -nice(Math.abs(min));
  } else {
    min = nice(min, true);
    if (originExtent[0] < min) {
      min = nice(min / 2, true);
    }
  }

  interval = max - min;

  return { min, max, interval };
}

/**
 * 将轴分割线固定在对应的条数
 */
function niceAxisExtent(axis: Axis2D, alignTo?: Axis2D) {
  const dvSplitLineNumber = (alignTo || axis)?.model?.get('dvSplitLineNumber') as number;
  if (!isValidSplitLineNumber(dvSplitLineNumber)) {
    return;
  }

  const splitNumber = dvSplitLineNumber - 1;
  const originExtent = getExtentWithBoundaryGap(axis);
  let [min, max] = originExtent;
  let interval: number;

  if (splitNumber > 1) {
    let toSplitNumber = splitNumber;
    let fixedSplitNumber = Infinity;

    while (fixedSplitNumber > splitNumber && toSplitNumber > 0) {
      // ! toSplitNumber === 1 时已达到最小分割数量了，此时不应再向下舍去，否则会出现 fixedSplitNumber > splitNumber 的情况
      interval = calcScientificNumber((max - min) / toSplitNumber, toSplitNumber > 1);
      fixedSplitNumber = Math.ceil(max / interval) - Math.floor(min / interval);

      toSplitNumber -= 1;
    }

    min = round(Math.floor(min / interval) * interval);
    max = round(Math.ceil(max / interval) * interval);

    while (fixedSplitNumber < splitNumber) {
      if (min === 0) {
        max += interval;
      } else if (max === 0) {
        min += -interval;
      } else if (originExtent[0] - min > max - originExtent[1]) {
        max += interval;
      } else {
        min += -interval;
      }
      fixedSplitNumber += 1;
    }
  } else {
    ({ min, max, interval } = niceAxisExtentOnOneSplitLine({ originExtent, min, max, interval }));
  }

  setAndUpdateCache(axis, { min, max, interval });
}

function getMaxMinInterval(averageBelowZero, averageAboveZero, splitNumber, max, min) {
  if (isInt(averageBelowZero)) {
    const interval = Math.max(
      averageAboveZero <= 0 ? 0 : nice(max / averageAboveZero),
      averageBelowZero <= 0 ? 0 : nice(-min / averageBelowZero)
    );
    return [averageAboveZero, averageBelowZero, interval];
  }
  const averageBelowZero1 = Math.max(Math.floor(averageBelowZero), 1);
  const averageAboveZero1 = splitNumber - averageBelowZero1;
  const interval1 = Math.max(
    averageAboveZero <= 0 ? 0 : nice(max / averageAboveZero1),
    averageBelowZero <= 0 ? 0 : nice(-min / averageBelowZero1)
  );

  const averageBelowZero2 = Math.ceil(averageBelowZero);
  const averageAboveZero2 = splitNumber - averageBelowZero2;
  const interval2 = Math.max(
    averageAboveZero <= 0 ? 0 : nice(max / averageAboveZero2),
    averageBelowZero <= 0 ? 0 : nice(-min / averageBelowZero2)
  );

  if (interval1 < interval2) {
    return [averageAboveZero1, averageBelowZero1, interval1];
  }
  return [averageAboveZero2, averageBelowZero2, interval2];
}

/**
 * 对齐刻度
 */
function alignScaleTicks(axisNeedsAlign: Axis2D[], alignTo: Axis2D) {
  const splitLineNumber = alignTo?.model?.get('dvSplitLineNumber') as number;
  if (typeof splitLineNumber === 'undefined' || !alignTo?.model?.get('interval')) {
    return;
  }

  const splitNumber = splitLineNumber - 1;
  const axisNeedsAlignZeroTick: Axis2D[] = [];

  axisNeedsAlign.forEach(axis => {
    niceAxisExtent(axis, alignTo);

    if (
      axis.model.getNeedCrossZero &&
      axis.model.getNeedCrossZero() &&
      axis.model.get('dvAlignZeroTick')
    ) {
      axisNeedsAlignZeroTick.push(axis);
    }
  });

  if (
    // ! 在数据范围跨 0 刻度时，至少要存在两个分割区域才能保证 0 刻度一定存在
    splitNumber > 1 &&
    alignTo.model.getNeedCrossZero &&
    alignTo.model.getNeedCrossZero() &&
    axisNeedsAlignZeroTick.length
  ) {
    axisNeedsAlignZeroTick.push(alignTo);

    let minTotal = 0;
    let maxTotal = 0;
    let zeroDown = 0;

    axisNeedsAlignZeroTick.forEach(axis => {
      minTotal += axis.model.get('min') as number;
      maxTotal += axis.model.get('max') as number;
      zeroDown += Math.floor(Math.abs(axis.model.get('min')) / axis.model.get('interval'));
    });

    if (minTotal === 0 || maxTotal === 0) {
      return;
    }

    const avgBelowZero = zeroDown / axisNeedsAlignZeroTick.length;
    const avgAboveZero = splitNumber - avgBelowZero;

    let [min, max] = getExtentWithBoundaryGap(alignTo);
    const [averageAboveZero, averageBelowZero] = getMaxMinInterval(
      avgBelowZero,
      avgAboveZero,
      splitNumber,
      max,
      min
    );

    axisNeedsAlignZeroTick.forEach(axis => {
      let [min, max] = getExtentWithBoundaryGap(axis);
      const interval = Math.max(
        averageAboveZero <= 0 ? 0 : nice(max / averageAboveZero),
        averageBelowZero <= 0 ? 0 : nice(-min / averageBelowZero)
      );
      min = -round(averageBelowZero * interval);
      max = round(averageAboveZero * interval);

      // reset
      setAndUpdateCache(axis, { min, max, interval });
    });
  }
}

type AxesMap = {
  x: Axis2D[];
  y: Axis2D[];
};

const originGridUpdateMethod = (Grid as typeof GridType).prototype.update;

(Grid as typeof GridType).prototype.update = function (
  ecModel: GlobalModel,
  api: ExtensionAPI,
  ...rest
) {
  const axesMap = this._axesMap as AxesMap;

  // ! 为了获取到真实数据的范围
  this._updateScale(ecModel, this.model);

  function preprocess(axes: Record<number, Axis2D>) {
    if (!axes) {
      return;
    }

    let alignTo: Axis2D;
    const axisNeedsAlign: Axis2D[] = [];

    (Object.keys(axes) as unknown as number[]).forEach(axesIndex => {
      const axis = axes[axesIndex];
      if (!needProcessSplitLineNumber(axis)) {
        /**
         * ! 某一点无数据时，要把max/min/interval都删掉
         * 否则会出现max/min/interval存在，但是cache不存在的情况，这会导致始终走不到后面的y轴处理逻辑，出现y轴刻度不再动态变化的现象
         * */
        if (axis?.model?.get('max') === axis?.model?.get('__cacheMax')) {
          delete axis.model.option.max;
        }
        if (axis?.model?.get('min') === axis?.model?.get('__cacheMin')) {
          delete axis.model.option.min;
        }
        if (axis?.model?.get('interval') === axis?.model?.get('__cacheInterval')) {
          delete axis.model.option.interval;
        }

        clearCache(axis);

        return;
      }

      clearCache(axis);

      // ! 多根轴刻度对齐策略和 echarts 的实现保持一致
      if (axis.model.get('alignTicks')) {
        axisNeedsAlign.push(axis);
      } else {
        niceAxisExtent(axis);
        alignTo = axis;
      }
    });

    if (axisNeedsAlign.length) {
      // ! 多根轴刻度对齐的参考轴选取和 echarts 实现保持一致
      if (!alignTo) {
        alignTo = axisNeedsAlign.pop();
        niceAxisExtent(alignTo);
      }

      alignScaleTicks(axisNeedsAlign, alignTo);
    }

    //
  }

  preprocess(axesMap.x);
  preprocess(axesMap.y);

  originGridUpdateMethod.call(this, ecModel, api, ...rest);
};

// ----------------------------------------------------------------

// ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

/**
 * * 为轴提供一个内边距配置项 `dvPadding`
 */
const originSetExtentMethod = (Axis as typeof AxisType).prototype.setExtent;

(Axis as typeof AxisType).prototype.setExtent = function (start: number, end: number, ...rest) {
  const dvPadding: [number, number] = this.model.get('dvPadding');
  if (typeof dvPadding !== 'undefined') {
    start += this.inverse ? -dvPadding[0] : dvPadding[0];
    end -= this.inverse ? -dvPadding[1] : dvPadding[1];
  }

  originSetExtentMethod.call(this, start, end, ...rest);
};

function installDVPadding(registers: EChartsExtensionInstallRegisters) {
  /**
   * ! 由于实现轴的内边距时修改了 extent 范围，在视觉效果上依然需要达到轴线和 grid 边对齐
   */
  registers.registerUpdateLifecycle('afterupdate', (ecModel, api) => {
    ecModel.eachComponent((compType, compModel: AxisModel) => {
      if (compType !== 'xAxis' && compType !== 'yAxis') {
        return;
      }

      const dvPadding: [number, number] = compModel.option;
      if (typeof dvPadding === 'undefined') {
        return;
      }

      const gridRect = compModel.getCoordSysModel().coordinateSystem.getRect();

      const compView = api.getViewOfComponentModel(compModel);

      (compView._axisGroup as Group)?.traverse(childEl => {
        if (childEl.anid !== 'line') {
          return;
        }

        // ! 丢失轴线动画
        childEl.stopAnimation(null, true);
        if (compType === 'xAxis') {
          childEl.attr({
            shape: {
              x1: gridRect.x,
              x2: gridRect.x + gridRect.width
            }
          });
        } else {
          childEl.attr({
            shape: {
              y1: gridRect.y,
              y2: gridRect.y + gridRect.height
            }
          });
        }

        //
      });

      //
    });

    //
  });

  //
}

// ----------------------------------------------------------------

export function install(registers: EChartsExtensionInstallRegisters) {
  installDVPadding(registers);
}
